/**
 * <TutorActivityDetailsModal> — la fiche d'une activité, vue par le tuteur (template).
 *
 * La branche tuteur de `TodoModal.js`, hors live comme dans le live (la carte d'un élève du live
 * ouvre ses activités par cette même fiche ; seul le centre du live a sa vue,
 * `<TutorLiveActivityView>`). C'est la fiche du parent (`<ActivityDetailsModal>`), composée, avec
 * ce que le tuteur y a de plus :
 *  - **ses gestes** (`UITutorActivityAction`, offerts par le view-model) : « Afficher à l'élève »
 *    (« Affichée chez l'élève ✓ » quand il l'a déjà devant lui), « Générer... », « Soumettre »,
 *    « Valider », « Redonner », « Modifier le titre », « Archiver » / « Restaurer ». Deux au plus
 *    en pied, dans l'ordre du legacy (afficher, valider, soumettre, redonner) ; les autres dans le
 *    menu de l'en-tête (⋮), comme le legacy y rangeait déjà « Archiver » et « Modifier le titre » ;
 *  - sous l'en-tête de l'activité, « · Quiz d'abord · » et « Retirer » (`QuizFirstMention`), et
 *    l'élève qui la regarde en ce moment (son avatar, en live) ;
 *  - la vue « Quizz », quand l'activité a un quiz de prérequis : le score et chaque réponse ;
 *  - sous la copie corrigée par Sam (la copie à pastilles de `<ActivityWork>`), l'avis du tuteur :
 *    « Cette correction est-elle juste ? » — « Oui » / « À revoir » (la mention « Correction IA · bêta »
 *    est retirée : la correction n'est plus visible du tuteur seul, Wajih, 02/11) ;
 *  - ses surfaces de décision, au tutoiement (archiver, valider, interrompre Sam, déjà affichée,
 *    ouverte par l'élève, commencée il y a moins d'une heure), et « Modifier le titre ».
 *
 * « Générer... » ouvre `<TutorGenerateActivityFlow>` à partir de cet exercice (« Générer à partir
 * de l'exo « … » »), la surface de l'écran ; la fiche ne le monte pas.
 *
 * Contrôlée : l'activité, la vue, les gestes, celui qui tourne, la décision ouverte et l'éditeur
 * du titre arrivent en props ; tout repart en callbacks.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { type UICorrectionFeedback } from "../../molecules/CorrectionFeedback"
import { Modal } from "../../molecules/Modal"
import { ThreeDotMenu } from "../../molecules/ThreeDotMenu"
import { TutorCorrectionReview } from "../../molecules/TutorCorrectionReview"
import { TutorQuizFirstMention } from "../../molecules/TutorQuizFirstMention"
import { ACTIVITY_ACTIONS, ACTIVITY_SHOWN_LABEL } from "../../molecules/TutorLiveCenterHead/tutorActivityActions"
import {
    QuizQuestionSignalSheet,
    type QuizQuestionSignalSheetProps,
} from "../../organisms/QuizQuestionSignalSheet/QuizQuestionSignalSheet"
import { TutorQuizReview, type TutorQuizReviewProps } from "../../organisms/TutorQuizReview"
import { ActivityDetailsModal, type ActivityDetailsModalProps } from "../ActivityDetailsModal/ActivityDetailsModal"
import { type UIQuizReview } from "../../../ports/primary/ui/types/studentQuizReview"
import { type UITutorActivityAction, type UITutorActivityDialog } from "../../../ports/primary/ui/types/tutorEleves"

/** Les gestes qui peuvent prendre le pied, dans l'ordre de la barre du legacy. */
const FOOTER_ORDER: UITutorActivityAction[] = ["show", "validate", "submit", "redo"]

type DialogText = {
    title: string
    message?: string
    confirmLabel: string
    cancelLabel?: string
    /** `notice` annonce (un seul bouton) ; `confirm` demande. */
    kind: "confirm" | "notice"
    danger?: boolean
}

function dialogText(dialog: UITutorActivityDialog, studentName: string): DialogText {
    switch (dialog) {
        case "archive":
            return {
                kind: "confirm",
                danger: true,
                title: "Veux-tu archiver cette activité ?",
                message: "Tu pourras la restaurer depuis les activités archivées.",
                confirmLabel: "Archiver",
                cancelLabel: "Annuler",
            }
        case "validate":
            return {
                kind: "confirm",
                title: "Valider l'activité",
                message: "Veux-tu marquer cette activité comme validée ?",
                confirmLabel: "Valider",
                cancelLabel: "Retour",
            }
        case "interruptSam":
            return {
                kind: "confirm",
                title: `${studentName} est en plein point d'avancement avec Sam`,
                message: "Souhaites-tu interrompre la conversation pour afficher cette activité ?",
                confirmLabel: "Oui, interrompre",
                cancelLabel: "Non",
            }
        case "alreadyShown":
            return { kind: "notice", title: "L'élève l'a déjà devant lui.", confirmLabel: "OK" }
        case "lockedByStudent":
            return {
                kind: "notice",
                title: "Impossible",
                message: "L'élève a actuellement ouvert cette activité.",
                confirmLabel: "OK",
            }
        case "startedRecently":
            return {
                kind: "notice",
                title: "Impossible",
                message: "L'activité a déjà été commencée par l'élève il y a moins d'une heure.",
                confirmLabel: "OK",
            }
    }
}

export type TutorActivityDetailsModalProps = Omit<
    ActivityDetailsModalProps,
    | "actions"
    | "onAction"
    | "submitting"
    | "dialog"
    | "onConfirmDialog"
    | "onCancelDialog"
    | "headerRight"
    | "headerAccessory"
    | "footer"
    | "workFooter"
    | "quiz"
    | "hidden"
> & {
    /** Le prénom de l'élève — les décisions le nomment. */
    studentName: string

    /** Les gestes offerts (le view-model les choisit). */
    actions?: UITutorActivityAction[]
    onAction?: (action: UITutorActivityAction) => void
    /** Le geste en cours — son bouton tourne. */
    submitting?: UITutorActivityAction | null
    /** L'élève a déjà cette activité devant lui : « Afficher » le dit. */
    shownToStudent?: boolean

    /** L'élève qui la regarde en ce moment, en live : son avatar sous l'en-tête. */
    viewingStudent?: { name: string; avatarUrl?: string } | null
    /** « · Quiz d'abord · Retirer » : l'élève passe par le quiz avant l'énoncé. */
    quizFirst?: { onRemove: () => void; removing?: boolean } | null
    /** Le quiz de prérequis, pour la vue « Quizz ». Absent : la vue dit qu'il n'y en a pas. */
    quizReview?: UIQuizReview | null
    /** L'avis du tuteur sur le quiz (« Ce quiz est-il utile ? »). Absent : pas de ligne. */
    quizFeedback?: TutorQuizReviewProps["feedback"]
    /** « Signaler » une question du quiz (son index). Absent : pas de lien. */
    onSignalQuizQuestion?: (index: number) => void
    /** La feuille du signalement, ouverte. Absent ou nul : pas de feuille. */
    quizSignal?: QuizQuestionSignalSheetProps | null
    /** L'avis du tuteur sur la correction de Sam. Absent : pas de ligne. */
    correctionFeedback?: {
        value: UICorrectionFeedback | null
        onChange: (value: UICorrectionFeedback) => void
        submitting?: boolean
    } | null

    dialog?: UITutorActivityDialog | null
    onConfirmDialog?: () => void
    onCancelDialog?: () => void

    /** « Modifier le titre » ouvert : la valeur en cours de saisie et son enregistrement. */
    titleEditor?: {
        value: string
        onChange: (value: string) => void
        onSave: () => void
        onCancel: () => void
        saving?: boolean
    } | null
}

export function TutorActivityDetailsModal(props: TutorActivityDetailsModalProps) {
    const {
        studentName,
        actions = [],
        onAction,
        submitting = null,
        shownToStudent = false,
        viewingStudent = null,
        quizFirst = null,
        quizReview = null,
        quizFeedback = null,
        onSignalQuizQuestion,
        quizSignal = null,
        correctionFeedback = null,
        dialog = null,
        onConfirmDialog,
        onCancelDialog,
        titleEditor = null,
        ...details
    } = props

    const footerActions = FOOTER_ORDER.filter((action) => actions.includes(action)).slice(0, 2)
    const menuActions = actions.filter((action) => !footerActions.includes(action))

    const footer =
        footerActions.length > 0 ? (
            <Row gap="2" justify="center" style={styles.wrap}>
                {footerActions.map((action, index) => {
                    const shown = action === "show" && shownToStudent
                    return (
                        <Button
                            key={action}
                            label={shown ? ACTIVITY_SHOWN_LABEL : ACTIVITY_ACTIONS[action].label}
                            iconName={shown ? undefined : ACTIVITY_ACTIONS[action].icon}
                            variant={index === 0 && !shown ? "solid" : "outline"}
                            color={action === "validate" || action === "submit" ? "success" : undefined}
                            loading={submitting === action}
                            disabled={submitting != null && submitting !== action}
                            onPress={() => onAction?.(action)}
                        />
                    )
                })}
            </Row>
        ) : null

    const menu =
        menuActions.length > 0 ? (
            <ThreeDotMenu
                accessibilityLabel="Plus d'actions"
                items={menuActions.map((action) => ({
                    icon: ACTIVITY_ACTIONS[action].icon,
                    text: ACTIVITY_ACTIONS[action].label,
                    onPress: () => onAction?.(action),
                }))}
            />
        ) : undefined

    const headerAccessory =
        quizFirst != null || viewingStudent != null ? (
            <Row gap="3" align="center" style={styles.wrap}>
                {quizFirst != null && (
                    <TutorQuizFirstMention onRemove={quizFirst.onRemove} removing={quizFirst.removing} />
                )}
                {viewingStudent != null && (
                    <Row gap="2" align="center">
                        <Avatar uri={viewingStudent.avatarUrl} name={viewingStudent.name} size={24} />
                        <Text size="text-sm" color="text-muted">
                            {`${viewingStudent.name} la regarde`}
                        </Text>
                    </Row>
                )}
            </Row>
        ) : undefined

    const workFooter =
        correctionFeedback != null ? (
            <TutorCorrectionReview
                value={correctionFeedback.value}
                onChange={correctionFeedback.onChange}
                submitting={correctionFeedback.submitting}
            />
        ) : undefined

    const openDialog = dialog != null ? dialogText(dialog, studentName) : null

    return (
        <>
            <ActivityDetailsModal
                {...details}
                headerRight={menu}
                headerAccessory={headerAccessory}
                footer={footer}
                workFooter={workFooter}
                quiz={
                    <TutorQuizReview
                        review={quizReview}
                        feedback={quizFeedback}
                        onSignalQuestion={onSignalQuizQuestion}
                    />
                }
                hidden={openDialog != null || titleEditor != null || quizSignal != null}
            />

            {quizSignal != null && <QuizQuestionSignalSheet {...quizSignal} />}

            {openDialog != null && (
                <ConfirmDialog
                    visible
                    title={openDialog.title}
                    message={openDialog.message}
                    confirmLabel={openDialog.confirmLabel}
                    cancelLabel={openDialog.cancelLabel}
                    hideCancel={openDialog.kind === "notice"}
                    danger={openDialog.danger === true}
                    onConfirm={() => (openDialog.kind === "notice" ? onCancelDialog?.() : onConfirmDialog?.())}
                    onCancel={() => onCancelDialog?.()}
                />
            )}

            {titleEditor != null && (
                <Modal
                    visible
                    onClose={titleEditor.onCancel}
                    title="Modifier le titre"
                    fitContent
                    footer={
                        <Button
                            label="Enregistrer"
                            fullWidth
                            disabled={titleEditor.value.trim() === "" || titleEditor.saving === true}
                            loading={titleEditor.saving}
                            onPress={titleEditor.onSave}
                        />
                    }
                >
                    <TextInput
                        label="Titre de l'activité"
                        value={titleEditor.value}
                        onChangeText={titleEditor.onChange}
                        placeholder="Entrer le titre..."
                        autoFocus
                    />
                </Modal>
            )}
        </>
    )
}

const styles = StyleSheet.create(() => ({
    wrap: { flexWrap: "wrap" },
}))
